import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ShieldCheck, Flame } from '@phosphor-icons/react';
import api, { mediaUrl } from '../lib/api';
import { useSettings } from '../context/SettingsContext';
import { toast } from 'sonner';
import Ico3 from '../components/Ico3';
import BrandLogo from '../components/Brand';
import ResponsiveImg from '../components/ResponsiveImg';
import { activeSocials, socialLink, socialIconSrc, socialHandle } from '../lib/socials';
import { DEFAULT_SPONSOR_CATEGORIES } from '../lib/sponsors';

const SectionTitle = ({ ico, children }) => (
  <div className="flex items-center gap-2 mb-4">
    <Ico3 name={ico} className="w-5 h-5" />
    <h2 className="text-[13px] font-semibold text-gray-300">{children}</h2>
  </div>
);

const openSponsor = (s) => {
  api.post(`/sponsors/${s.id}/click`).catch(() => {});
  if (s.url) {
    window.open(s.url.startsWith('http') ? s.url : `https://${s.url}`, '_blank', 'noopener');
  } else {
    toast.info(`${s.name} için yönlendirme linki henüz eklenmedi.`);
  }
};

const Logo = ({ s, variant = 'card' }) => (
  s.logo_url ? (
    <span className={`sponsor-logo sponsor-logo--${variant}`}>
      <img src={mediaUrl(s.logo_url)} alt={s.name} />
    </span>
  ) : null
);

const BannerCarousel = ({ items }) => {
  const navigate = useNavigate();
  const { site } = useSettings();
  const [idx, setIdx] = useState(0);
  const interval = Math.min(60, Math.max(2, Number(site?.slider_interval) || 5)) * 1000;

  useEffect(() => {
    if (items.length < 2) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % items.length), interval);
    return () => clearInterval(t);
  }, [items.length, interval]);

  const cta = (b) => {
    if (!b.link) return toast.info('Bu kampanya için link henüz eklenmedi.');
    if (b.link.startsWith('/')) navigate(b.link);
    else window.open(b.link.startsWith('http') ? b.link : `https://${b.link}`, '_blank', 'noopener');
  };

  const cur = idx % items.length;

  return (
    <div data-testid="banner-carousel" className="slot-slider relative rounded-2xl overflow-hidden border border-white/10 bg-bh-canvas">
      {items.map((b, i) => (
        <div
          key={b.id}
          data-testid={`promo-banner-${b.id}`}
          className={`absolute inset-0 flex group transition-opacity duration-700 ${i === cur ? 'opacity-100 z-10' : 'opacity-0 z-0 pointer-events-none'}`}
        >
          <ResponsiveImg fill desktop={b.img} mobile={b.img_mobile} alt="" />
          <div className="absolute inset-0 bg-gradient-to-r from-bh-canvas/90 via-bh-canvas/40 to-transparent" />
          <div className="relative z-10 w-full h-full min-h-[200px] sm:min-h-[340px] p-5 sm:p-9 flex flex-col justify-between">
            <div className="max-w-[92%] sm:max-w-[70%]">
              {b.badge && (
                <span className={`inline-block px-3 py-1 rounded-lg text-[11px] font-display font-extrabold uppercase tracking-wider ${b.badge_style === 'gold' ? 'bg-bh-gold text-black' : 'bg-bh-accent text-white'}`}>{b.badge}</span>
              )}
              <p className="mt-4 sm:mt-5 font-display font-extrabold leading-[1.15] tracking-tight text-[1.25rem] sm:text-4xl lg:text-[2.4rem] text-white break-words">{b.title}</p>
            </div>
            <button
              data-testid={`promo-banner-cta-${b.id}`}
              onClick={() => cta(b)}
              className="self-start btn-accent px-5 sm:px-7 py-2.5 sm:py-3 rounded-xl text-sm"
            >
              {b.cta_label || 'Katıl'}
            </button>
          </div>
        </div>
      ))}
      {items.length > 1 && (
        <div className="absolute bottom-4 left-5 z-20 flex gap-1.5">
          {items.map((_, i) => (
            <button
              key={i}
              data-testid={`banner-dot-${i}`}
              onClick={() => setIdx(i)}
              className={`h-2 rounded-full transition-all ${i === cur ? 'bg-white w-5' : 'bg-white/30 w-2 hover:bg-white/60'}`}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const BannerStrip = ({ items }) => {
  const navigate = useNavigate();
  const { site } = useSettings();
  const [idx, setIdx] = useState(0);
  const interval = 5000;

  useEffect(() => {
    if (items.length < 2) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % items.length), interval);
    return () => clearInterval(t);
  }, [items.length]);

  if (!items.length) return null;
  const cur = idx % items.length;
  const go = (b) => {
    if (!b?.link) return toast.info('Bu reklam için link henüz eklenmedi.');
    if (b.link.startsWith('/')) navigate(b.link);
    else window.open(b.link.startsWith('http') ? b.link : `https://${b.link}`, '_blank', 'noopener');
  };

  return (
    <section className="rise-in-d1" data-testid="banner-strip">
      <div className="relative w-full rounded-2xl slot-strip overflow-hidden border border-white/10 cursor-pointer group bg-bh-canvas" onClick={() => go(items[cur])}>
        {items.map((b, i) => (
          <div
            key={b.id || i}
            data-testid={`banner-strip-item-${b.id}`}
            className={`absolute inset-0 transition-opacity duration-500 ${i === cur ? 'opacity-100 z-10' : 'opacity-0 z-0 pointer-events-none'}`}
          >
            {(b.img || b.img_mobile) ? (
              <ResponsiveImg fill desktop={b.img} mobile={b.img_mobile} alt={b.title || ''} />
            ) : (
              <div className="absolute inset-0 bg-bh-surface flex items-center px-5">
                <p className="font-display font-extrabold text-sm sm:text-base text-white truncate">{b.title || 'Reklam'}</p>
              </div>
            )}
          </div>
        ))}
      </div>
    </section>
  );
};

const PopularBadge = () => (
  <span data-testid="popular-badge" className="inline-flex items-center gap-0.5 px-1.5 sm:px-2 py-0.5 rounded-full bg-bh-crimson/15 border border-bh-crimson/40 text-bh-crimson text-[8px] sm:text-[9px] font-bold uppercase tracking-wider shrink-0 leading-none">
    <Flame className="w-2.5 h-2.5" /> Popüler
  </span>
);

const neonStyle = (s) => (s.neon_color ? { '--neon-c': s.neon_color } : undefined);

const bonusColor = (s) => (s.bonus_color ? { color: s.bonus_color } : undefined);
const descColor = (s) => {
  const c = s.desc_color || s.neon_color;
  return c ? { color: c } : undefined;
};


const SponsorStrip = ({ s }) => {
  const hasBanner = Boolean(s.banner_img || s.banner_img_mobile);
  const showCta = s.show_cta !== false;
  return (
    <div
      data-testid={`sponsor-strip-${s.id}`}
      style={neonStyle(s)}
      className={`sponsor-live neon-frame relative rounded-xl overflow-hidden border border-white/10 flex items-center group cursor-pointer ${
        hasBanner ? 'slot-sponsor-banner w-full' : 'h-auto min-h-[6.75rem] sm:min-h-[7.25rem]'
      }`}
      onClick={() => openSponsor(s)}
    >
      {hasBanner ? (
        <ResponsiveImg fill desktop={s.banner_img} mobile={s.banner_img_mobile} alt="" />
      ) : null}
      {!hasBanner && <div className="absolute inset-0 bg-gradient-to-r from-black/75 via-black/35 to-black/75" />}
      <span className="sponsor-live-fx" aria-hidden="true" />
      {hasBanner ? (
        <div className="relative flex items-center justify-end w-full h-full px-3 sm:px-8 pointer-events-none">
          {showCta && (
            <button
              type="button"
              data-testid={`sponsor-strip-cta-${s.id}`}
              className="btn-accent px-3 sm:px-5 py-2 sm:py-2.5 rounded-lg text-[11px] sm:text-xs shrink-0 pointer-events-auto shadow-lg shadow-black/40"
            >
              Katıl
            </button>
          )}
        </div>
      ) : (
        <div className="relative flex items-center w-full px-2.5 sm:px-8 gap-1.5 sm:gap-4">
          <div className="flex items-center gap-2 sm:gap-4 min-w-0 flex-1">
            <Logo s={s} variant="strip" />
            <div className="min-w-0 flex-1">
              {s.name ? <p className="sponsor-name min-w-0 truncate text-base sm:text-[22px] text-white">{s.name}</p> : null}
              <p className={`sponsor-bonus ${s.bonus_color ? '' : 'text-gray-300'}`} style={bonusColor(s)}>{s.bonus}</p>
            </div>
          </div>
          {s.code && (
            <div className="hidden md:block font-display font-black text-xl lg:text-2xl tracking-wide text-white/95">
              KOD: <span className="text-bh-gold">{s.code}</span>
            </div>
          )}
          <div className="flex flex-col items-end justify-center gap-1 shrink-0">
            {s.popular ? <PopularBadge /> : null}
            {showCta && (
              <button type="button" data-testid={`sponsor-strip-cta-${s.id}`} className="btn-accent px-2 sm:px-5 py-1.5 sm:py-2.5 rounded-lg text-[10px] sm:text-xs shrink-0">Katıl</button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const VipCard = ({ s }) => (
  <div data-testid={`sponsor-card-${s.id}`} style={neonStyle(s)} className="neon-frame panel panel-hover top-light h-full px-3 pt-3 pb-4 text-center flex flex-col items-center relative">
    {s.popular && <div className="absolute top-2.5 right-2.5"><PopularBadge /></div>}
    <Logo s={s} variant="card" />
    <p className="sponsor-name text-[15px] mt-2">{s.name}</p>
    <p className={`sponsor-bonus mt-2 ${s.bonus_color ? '' : 'text-bh-emerald'}`} style={bonusColor(s)}>{s.bonus}</p>
    {s.desc ? (
      <p className="sponsor-desc mt-1.5 line-clamp-2" style={descColor(s)}>{s.desc}</p>
    ) : null}
    <div className="w-full mt-auto pt-4">
      <button
        data-testid={`sponsor-claim-btn-${s.id}`}
        onClick={() => openSponsor(s)}
        className="btn-accent w-full py-2 rounded-lg text-xs"
      >
        Bonusu Al
      </button>
    </div>
  </div>
);

const SponsorRow = ({ s, cta = 'Katıl', ghost = false, shield = false, live = false }) => (
  <div data-testid={`sponsor-card-${s.id}`} style={neonStyle(s)} className={`neon-frame panel panel-hover px-2.5 py-3 sm:p-4 flex items-center gap-2 sm:gap-4 ${live ? 'sponsor-live overflow-hidden' : ''}`}>
    {live ? <span className="sponsor-live-fx" aria-hidden="true" /> : null}
    <Logo s={s} variant="row" />
    <div className="min-w-0 flex-1">
      <div className="flex items-center gap-1.5">
        {s.name ? <p className="sponsor-name text-[15px] truncate">{s.name}</p> : null}
        {shield && <ShieldCheck className="w-3.5 h-3.5 text-bh-emerald shrink-0" />}
      </div>
      <p className={`sponsor-bonus ${s.bonus_color ? '' : (ghost ? 'text-gray-500' : 'text-bh-emerald')}`} style={bonusColor(s)}>{s.bonus}</p>
    </div>
    <div className="flex flex-col items-end justify-center gap-1 shrink-0">
      {s.popular ? <PopularBadge /> : null}
      <button
        data-testid={`sponsor-claim-btn-${s.id}`}
        onClick={() => openSponsor(s)}
        className={`${ghost ? 'btn-ghost hover:!border-bh-accent/50' : 'btn-accent'} px-2 sm:px-4 py-1.5 sm:py-2 rounded-lg text-[10px] sm:text-xs shrink-0 ${ghost ? 'font-bold' : ''}`}
      >
        {cta}
      </button>
    </div>
  </div>
);

const CAT_ICO = { strip: 'gem', split: 'gem', cards: 'trophy', rows: 'home' };

export default function Home() {
  const { modules, site } = useSettings();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const isSponsorPage = pathname === '/sponsorlar';
  const [sponsors, setSponsors] = useState({});
  const [categories, setCategories] = useState(DEFAULT_SPONSOR_CATEGORIES);
  const [banners, setBanners] = useState([]);
  const [strips, setStrips] = useState([]);
  const socials = activeSocials(site);

  useEffect(() => {
    api.get('/sponsors').then((r) => setSponsors(r.data || {})).catch(() => {});
    api.get('/sponsor-categories').then((r) => {
      if (Array.isArray(r.data) && r.data.length) setCategories(r.data);
    }).catch(() => {});
    api.get('/banners').then((r) => setBanners(r.data)).catch(() => {});
    api.get('/sponsor-strips').then((r) => setStrips(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  }, []);

  const filt = (list) => (list || []);
  const slotOf = (b) => Number(b.slot ?? 1);
  const slot1 = banners.filter((b) => slotOf(b) !== 2);
  const slot2 = banners.filter((b) => slotOf(b) === 2);
  const heroText = String(site?.hero_text || '').trim();
  const heroImg = String(site?.hero_image || '').trim();
  const heroImgMobile = String(site?.hero_image_mobile || '').trim();
  const hasHero = Boolean(heroImg || heroImgMobile);
  const heroCta1Label = String(site?.hero_cta1_label || '').trim() || 'Sponsorları Gör';
  const heroCta1Link = String(site?.hero_cta1_link || '').trim() || '/sponsorlar';
  const heroCta2Label = String(site?.hero_cta2_label || '').trim() || 'Casino Oyunları';
  const heroCta2Link = String(site?.hero_cta2_link || '').trim() || '/oyunlar';
  const goHero = (link) => {
    const raw = String(link || '').trim();
    if (!raw) return;
    if (raw.startsWith('/')) navigate(raw);
    else window.open(raw.startsWith('http') ? raw : `https://${raw}`, '_blank', 'noopener');
  };

  return (
    <div className="space-y-10">
      {!isSponsorPage && socials.length > 0 && (
      <section className="rise-in-d1 sm:hidden">
        <div data-testid="home-socials" className="home-socials">
          {socials.map((s, i) => (
            <a
              key={`${s.kind}-${i}`}
              href={socialLink(s)}
              target="_blank"
              rel="noopener noreferrer"
              title={socialHandle(s)}
              data-testid={`home-social-${s.kind}`}
              className={`home-social-card is-${s.kind}`}
            >
              <img src={socialIconSrc(s.kind)} alt="" className="home-social-ico" />
              <span className="home-social-handle">{socialHandle(s)}</span>
            </a>
          ))}
        </div>
      </section>
      )}

      {!isSponsorPage && modules.brand_hero !== false && (
        <section className="rise-in relative overflow-hidden rounded-2xl border border-white/10 min-h-0 sm:min-h-[280px]" data-testid="brand-hero" style={{ background: 'linear-gradient(115deg, var(--bh-canvas) 0%, var(--bh-surface) 55%, var(--bh-surface-2) 100%)' }}>
          <div className="absolute inset-0 pointer-events-none" style={{ background: 'radial-gradient(ellipse at 78% 40%, rgb(var(--bh-accent-rgb) / 0.14), transparent 55%)' }} />
          {hasHero ? (
            <div className="pointer-events-none absolute inset-y-0 right-0 z-[1] flex items-center sm:items-end justify-end w-[38%] sm:w-[42%] md:w-auto max-w-[168px] sm:max-w-[260px] md:max-w-none pr-1 sm:pr-1 lg:pr-6">
              <ResponsiveImg
                desktop={heroImg}
                mobile={heroImgMobile}
                alt=""
                className="h-[82%] sm:h-[92%] max-h-[210px] sm:max-h-[280px] md:h-56 lg:h-72 w-auto object-contain object-center sm:object-bottom select-none"
                style={{ filter: 'drop-shadow(0 8px 24px rgba(0,0,0,0.35))' }}
              />
            </div>
          ) : null}
          <div className={`relative z-10 ${hasHero ? 'pt-3 pb-3 pl-4 pr-3 sm:py-8 sm:pl-5 sm:pr-8 max-w-[64%] sm:max-w-lg md:max-w-xl' : 'py-8 px-5 sm:px-12 min-h-[240px] sm:min-h-[280px] flex flex-col items-center justify-center text-center'}`}>
            {hasHero ? (
              <div className="flex flex-col items-start text-left w-full min-w-0">
                <BrandLogo className="block h-12 sm:h-[3.75rem] lg:h-16 w-auto max-w-[16rem] object-contain ml-0 mr-auto" />
                {heroText ? <p className="mt-2.5 sm:mt-3.5 text-[13px] sm:text-base text-gray-300 leading-relaxed text-left w-full pr-2">{heroText}</p> : null}
                <div className="mt-2 mb-0 flex flex-row flex-wrap items-center gap-1.5 sm:hidden w-full max-w-full">
                  <button data-testid="hero-sponsors-btn" onClick={() => goHero(heroCta1Link)} className="btn-accent px-2 py-1 rounded-md text-[9px] font-bold inline-flex items-center justify-center gap-1 whitespace-nowrap shrink-0"><img src="/icons3d/gem.png" alt="" className="w-3 h-3 object-contain" /> {heroCta1Label}</button>
                  <button data-testid="hero-games-btn" onClick={() => goHero(heroCta2Link)} className="btn-ghost px-2 py-1 rounded-md text-[9px] font-bold inline-flex items-center justify-center gap-1 whitespace-nowrap shrink-0"><img src="/icons3d/roulette.png" alt="" className="w-3 h-3 object-contain" /> {heroCta2Label}</button>
                </div>
              </div>
            ) : (
              <BrandLogo className="h-[9.5rem] sm:h-[12.5rem] lg:h-[14.5rem] w-auto max-w-[92%] object-contain" style={{ filter: 'drop-shadow(0 6px 20px rgb(var(--bh-accent-rgb) / 0.35))' }} />
            )}
            {!hasHero && heroText ? <p className="mt-3 sm:mt-4 text-sm sm:text-base text-gray-300 leading-relaxed">{heroText}</p> : null}
            <div className={`${hasHero ? 'mt-6 hidden sm:flex' : 'mt-6 flex'} flex-col sm:flex-row flex-wrap gap-3`}>
              <button data-testid={hasHero ? 'hero-sponsors-btn-desktop' : 'hero-sponsors-btn'} onClick={() => goHero(heroCta1Link)} className="btn-accent px-6 py-3 rounded-xl text-sm font-bold inline-flex items-center justify-center gap-2 w-full sm:w-auto"><img src="/icons3d/gem.png" alt="" className="w-5 h-5 object-contain" /> {heroCta1Label}</button>
              <button data-testid={hasHero ? 'hero-games-btn-desktop' : 'hero-games-btn'} onClick={() => goHero(heroCta2Link)} className="btn-ghost px-6 py-3 rounded-xl text-sm font-bold inline-flex items-center justify-center gap-2 w-full sm:w-auto">
                <img src="/icons3d/roulette.png" alt="" className="w-5 h-5 object-contain" /> {heroCta2Label}
              </button>
            </div>
          </div>
        </section>
      )}

      {!isSponsorPage && modules.banners !== false && banners.length > 0 && (
        <section className={`rise-in grid grid-cols-1 ${slot1.length > 0 && slot2.length > 0 ? 'xl:grid-cols-2' : ''} gap-4`}>
          {slot1.length > 0 && <BannerCarousel items={slot1} />}
          {slot2.length > 0 && <BannerCarousel items={slot2} />}
        </section>
      )}

      {isSponsorPage && site?.sponsor_strip_enabled !== false && strips.length > 0 && <BannerStrip items={strips} />}

      {categories.map((cat) => {
        const list = filt(sponsors[cat.key]);
        const modKey = `sponsors_${cat.key}`;
        if (modules[modKey] === false || !list.length) return null;
        const layout = cat.layout || (cat.key === 'main' ? 'strip' : cat.key === 'vip' ? 'cards' : 'rows');
        const ico = CAT_ICO[layout] || 'trophy';
        return (
          <section key={cat.key} className="rise-in-d1" data-testid={`sponsor-section-${cat.key}`}>
            <SectionTitle ico={ico}>{cat.label}</SectionTitle>
            {layout === 'strip' ? (
              <>
                <div className="space-y-3">
                  {list.slice(0, 2).map((s) => (
                    <SponsorStrip key={s.id} s={s} />
                  ))}
                </div>
                {list.length > 2 && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
                    {list.slice(2).map((s) => <SponsorRow key={s.id} s={s} live />)}
                  </div>
                )}
              </>
            ) : layout === 'split' ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {list.map((s) => <SponsorRow key={s.id} s={s} live />)}
              </div>
            ) : layout === 'cards' ? (
              <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
                {list.map((s) => <VipCard key={s.id} s={s} />)}
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
                {list.map((s) => (
                  <SponsorRow key={s.id} s={s} cta="İncele" ghost shield={cat.key === 'trusted'} />
                ))}
              </div>
            )}
          </section>
        );
      })}

    </div>
  );
}
